import { reactive, ref } from "vue";
import { getJSON } from "@api/request";
import { getActivityApi } from "@utils";
import { getRewardByConfigId } from "@/config/rewards";

const showCurErrorTips = ref();
const showSuccessTips = ref();

const physicalAwardPopupState = ref(false);
const physicalDoubleCheckPopupState = ref(false);

// 背包相关状态
const awardLogList = ref<any[]>([]);
const total = ref(0);

// 实物奖励相关状态
const showToast = ref(false);

// 虚拟奖励兑换码
export const virtualAwardDetailPopupState = ref(false);
export const virtualAwardDetailCode = ref("");

// 当前奖励详情（用于礼包码弹窗）
export const currentAwardDetail = reactive<{
  icon: string;
  name: string;
  rewardValue: string;
}>({
  icon: "",
  name: "",
  rewardValue: "",
});

// 抽奖结果列表（用于抽奖结果弹窗）
export const lotteryAwardList = ref<any[]>([]);
export const lotteryResultPopupState = ref(false);

// 常量定义
const TOAST_DISPLAY_DURATION = 1500; // Toast 显示时长（毫秒）
const MAX_NAME_LENGTH = 20;
const MAX_PHONE_LENGTH = 30;
const MAX_ADDRESS_LENGTH = 200;

const viewCode = (item: any) => {
  // 使用 rewardConfigId 获取奖励信息
  const rewardInfo = getRewardByConfigId(item.rewardConfigId);

  if (!rewardInfo) {
    showSuccessTips.value?.('獎勵配置不存在');
    return;
  }

  // 设置当前奖励详情并显示弹窗（rewardValue 可以为空）
  currentAwardDetail.icon = rewardInfo.icon;
  currentAwardDetail.name = rewardInfo.name;
  currentAwardDetail.rewardValue = item.rewardValue || ''; // 即使为空也展示

  virtualAwardDetailPopupState.value = true;
};

// 获取背包列表
const fetchAwardList = async () => {
  let res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "listMyBackpack"),
    {}
  );

  if (res.result === 1) {
    awardLogList.value = res.data.rewardList || [];
    total.value = res.data.totalCount || 0;
  } else {
    showCurErrorTips.value?.(res.code);
  }

  return res;
};

// 初始化背包列表
const initAwardList = async () => {
  awardLogList.value = [];
  await fetchAwardList();
};

// 重置背包列表
const resetAwardList = () => {
  awardLogList.value = [];
};

/**
 * 获取奖励状态文本和是否可查看
 * @param item 奖励项
 * @returns 奖励状态信息
 */
const getRewardStatus = (item: any) => {
  const rewardStatus = item.rewardStatus;
  const rewardType = item.rewardType;
  const isSendSerial = rewardType === "sendSerial"; // 虚拟奖励

  // rewardStatus: 1=已发放, 2=待发放
  if (rewardStatus === 2) {
    return {
      text: "待發放",
      canViewCode: false,
    };
  } else {
    // rewardStatus === 1，已发放
    return {
      text: isSendSerial ? "查看禮包碼>>" : "已發放",
      canViewCode: isSendSerial,
    };
  }
};

const physicalAwardUserState = reactive({
  phone: "",
  name: "",
  address: "",
  hasPhoneError: false,
  hasNameError: false,
  hasAddressError: false,
});

// 虚拟奖励
const getVirtualAward = async () => {
  const res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "virtualAward"),
    {}
  );
  if (res.result === 1) {
    showSuccessTips.value?.("請至【我的獎品】頁面查看禮包碼");
    // 领奖成功后刷新背包列表
    await initAwardList();
    // 重新请求basic接口
    return true;
  } else {
    showCurErrorTips.value?.(res?.code);
    return false;
  }
};

const getPhysicalAward = async (params: {
  phone: string;
  name: string;
  address: string;
}) => {
  const res = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "materialAward"),
    {
      params: JSON.stringify(params),
    }
  );
  return res;
};

// 实物奖励
const fillPhysicalAward = () => {
  physicalAwardPopupState.value = true;
};

// 字段验证辅助函数
const validateField = (
  value: string,
  maxLength: number,
  errorStateKey: "hasNameError" | "hasPhoneError" | "hasAddressError"
): boolean => {
  const isValid = value && value.length > 0 && value.length <= maxLength;
  (physicalAwardUserState as any)[errorStateKey] = !isValid;
  return isValid;
};

const doubleCheckPhysicalAward = () => {
  const isNameValid = validateField(
    physicalAwardUserState.name,
    MAX_NAME_LENGTH,
    "hasNameError"
  );
  if (!isNameValid) return;

  const isPhoneValid = validateField(
    physicalAwardUserState.phone,
    MAX_PHONE_LENGTH,
    "hasPhoneError"
  );
  if (!isPhoneValid) return;

  const isAddressValid = validateField(
    physicalAwardUserState.address,
    MAX_ADDRESS_LENGTH,
    "hasAddressError"
  );
  if (!isAddressValid) return;

  physicalAwardPopupState.value = false;
  physicalDoubleCheckPopupState.value = true;
};

const cancelDoubleCheckPhysicalAward = () => {
  physicalDoubleCheckPopupState.value = false;
  physicalAwardPopupState.value = true;
};

const submitPhysicalAward = async () => {
  const res = await getPhysicalAward({
    phone: encodeURIComponent(physicalAwardUserState.phone),
    name: encodeURIComponent(physicalAwardUserState.name),
    address: encodeURIComponent(physicalAwardUserState.address),
  });
  // let res = {result: 1}
  if (res.result === 1) {
    showToast.value = true;
    setTimeout(async () => {
      showToast.value = false;
      physicalDoubleCheckPopupState.value = false;
      resetPhysicalAwardForm();
      // 领奖成功后刷新背包列表
      await initAwardList();
    }, TOAST_DISPLAY_DURATION);
    return true;
  } else {
    showCurErrorTips.value?.(res?.code);
    return false;
  }
};

// 重置实物奖励表单
const resetPhysicalAwardForm = () => {
  physicalAwardUserState.phone = "";
  physicalAwardUserState.name = "";
  physicalAwardUserState.address = "";
  physicalAwardUserState.hasPhoneError = false;
  physicalAwardUserState.hasNameError = false;
  physicalAwardUserState.hasAddressError = false;
};

export default function useGetAward(
  funcShowSuccessTips = null,
  funcShowCurErrorTips = null
) {
  if (funcShowSuccessTips) {
    showSuccessTips.value = funcShowSuccessTips;
  }
  if (funcShowCurErrorTips) {
    showCurErrorTips.value = funcShowCurErrorTips;
  }
  return {
    // 背包相关
    awardLogList,
    total,
    initAwardList,
    resetAwardList,
    getRewardStatus, // 导出状态判断函数
    // 领奖相关
    physicalAwardPopupState,
    physicalDoubleCheckPopupState,
    physicalAwardUserState,
    fillPhysicalAward,
    doubleCheckPhysicalAward,
    cancelDoubleCheckPhysicalAward,
    submitPhysicalAward,
    resetPhysicalAwardForm,
    getVirtualAward,
    getPhysicalAward,
    showToast,
    virtualAwardDetailPopupState,
    virtualAwardDetailCode,
    currentAwardDetail,
    viewCode,
  };
}
